<template>
  <div class="mt-8" v-show="showDetails">
    <dispute-notification v-if="stageDispute">
      {{ stageDispute.comment }}
    </dispute-notification>

    <el-form
      @keyup.enter.native="validateForm('kycDetailsForm')"
      @submit.native.prevent="validateForm('kycDetailsForm')"
      label-position="top"
      :model="kycDetailsForm"
      :rules="rules"
      ref="kycDetailsForm"
      test-id="form"
    >
      <div class="flex flex-col gap-1 bg-white">
        <div
          class="w-full flex flex-col md:flex-row md:gap-6 md:justify-between"
        >
          <el-form-item class="w-full" prop="firstName">
            <p>First Name <span class="text-error-10">*</span></p>
            <el-input
              class="w-full"
              v-model="kycDetailsForm.firstName"
              test-id="firstName"
            ></el-input>
          </el-form-item>

          <el-form-item class="w-full" prop="lastName">
            <p>Last Name <span class="text-error-10">*</span></p>
            <el-input
              class="w-full"
              v-model="kycDetailsForm.lastName"
              test-id="lastName"
            ></el-input>
          </el-form-item>
        </div>

        <el-form-item class="w-full" prop="email">
          <p>Entity Contact Email <span class="text-error-10">*</span></p>
          <el-input class="w-full" v-model="kycDetailsForm.email"></el-input>
        </el-form-item>

        <el-form-item class="w-full" prop="bvn">
          <p>BVN</p>
          <h6 class="text-gray-110 italic text-xs -mt-2 mb-3">
            For Nigerians Only
          </h6>
          <el-input class="w-full" v-model="kycDetailsForm.bvn"></el-input>
        </el-form-item>

        <el-form-item prop="phoneNumber">
          <p>Phone Number <span class="text-error-10">*</span></p>
          <el-input
            type="number"
            v-model="kycDetailsForm.phoneNumber"
            placeholder="Enter phone number"
            onkeypress="return event.charCode != 45"
            min="0"
          >
          </el-input>
        </el-form-item>

        <div
          class="w-full flex flex-col md:flex-row md:gap-6 md:justify-between"
        >
          <el-form-item class="w-full md:w-1/2" prop="gender">
            <p>Gender <span class="text-error-10">*</span></p>
            <el-select
              class="w-full"
              v-model="kycDetailsForm.gender"
              placeholder="Select"
            >
              <el-option
                v-for="(gender, index) in genders"
                :key="index"
                :label="gender.label"
                :value="gender.value"
              >
                {{ gender.label }}
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item prop="dateOfBirth">
            <p>Date of Birth <span class="text-error-10">*</span></p>
            <el-date-picker
              v-model="kycDetailsForm.dateOfBirth"
              type="date"
              placeholder="YYYY-MM-DD"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              full-width="fullWidth"
            />
          </el-form-item>
        </div>

        <div
          class="w-full flex flex-col md:flex-row md:gap-6 md:justify-between"
        >
          <el-form-item class="w-full md:w-1/2" prop="idType">
            <p>Valid ID Type <span class="text-error-10">*</span></p>
            <el-select
              class="w-full"
              v-model="kycDetailsForm.idType"
              placeholder="Select"
            >
              <el-option
                v-for="(gender, index) in validIdTypes"
                :key="index"
                :label="gender.label"
                :value="gender.value"
              >
                {{ gender.label }}
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item class="w-full md:w-1/2" prop="idNumber">
            <p>ID Number<span class="text-error-10">*</span></p>
            <el-input v-model="kycDetailsForm.idNumber"></el-input>
          </el-form-item>
        </div>

        <button
          class="text-white bg-primary rounded-lg mt-2 h-12 md:h-14 w-44 md:w-52 hover:opacity-90"
          type="submit"
          :disabled="isLoading || isApproved"
        >
          <spinner
            v-if="isLoading"
            size="small"
            :spacing="40"
            :line-fg-color="$primaryColor"
          />
          <span v-else>Confirm</span>
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import {
  GENDER_TYPES,
  PHONE_NUMBER_REGEX,
  VALID_ID_TYPES
} from '@/utils/constants.js'
import { ValidateKycInput } from '@/utils/helpers.js'
import kyc from '@/mixins/kyc.js'

export default {
  name: 'UserProfile',
  mixins: [kyc],
  components: {
    DisputeNotification: () =>
      import('@/components/v2/completeKycForm/common/DisputeNotification.vue')
  },
  props: {
    business: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    var validatePhoneNumber = (rule, value, callback) => {
      if (!PHONE_NUMBER_REGEX.test(value)) {
        callback(new Error('Please input a valid phone number'))
      } else {
        callback()
      }
    }
    return {
      genders: GENDER_TYPES,
      validIdTypes: VALID_ID_TYPES,
      isLoading: false,
      pickerOptions: {
        disabledDate: this.disabledDate
      },
      kycStep: 1,
      kycDetailsForm: {
        firstName: '',
        lastName: '',
        email: '',
        bvn: '',
        phoneNumber: '',
        gender: '',
        dateOfBirth: '',
        idType: '',
        idNumber: ''
      },
      rules: {
        firstName: [
          {
            required: true,
            message: 'Please input first name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        lastName: [
          {
            required: true,
            message: 'Please input last name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        email: [
          {
            required: true,
            type: 'email',
            message: 'Please enter a valid email address',
            trigger: ['blur', 'change']
          }
        ],
        bvn: [
          {
            message: 'Please enter BVN',
            trigger: ['blur', 'change']
          },
          {
            max: 11,
            message: 'Invalid BVN',
            trigger: ['blur', 'change']
          }
        ],
        companyType: [
          {
            required: true,
            message: 'Please input company type',
            trigger: ['blur', 'change']
          }
        ],
        phoneNumber: [
          {
            required: true,
            message: 'Please enter phone number',
            trigger: ['blur', 'change']
          },
          {
            max: 14,
            message: 'Invalid phone number ',
            trigger: ['blur', 'change']
          },
          { validator: validatePhoneNumber, trigger: ['blur', 'change'] }
        ],
        gender: [
          {
            required: true,
            message: 'Please choose a gender',
            trigger: ['blur', 'change']
          }
        ],
        dateOfBirth: [
          {
            required: true,
            message: 'Please enter date of birth',
            trigger: ['blur', 'change']
          }
        ],
        idType: [
          {
            required: true,
            message: 'Please choose an ID type',
            trigger: ['blur', 'change']
          }
        ],
        idNumber: [
          {
            required: true,
            message: 'Please enter a valid ID number',
            trigger: ['blur', 'change']
          },
          {
            max: 23,
            message: 'Invalid ID Number',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  computed: {
    showDetails () {
      return this.business.show && this.business.step === this.kycStep
    },
    userInfo () {
      return this.$store.state.kyc.kycDetails
    },
    isApproved () {
      return this.approvedStages.includes(this.business.step)
    }
  },
  methods: {
    sliceName (name) {
      return name.split(' ')
    },
    validateForm (kycDetailsForm) {
      this.$refs[kycDetailsForm].validate((valid) => {
        if (valid) {
          this.submitKycStepOne()
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    submitKycStepOne () {
      const contactInformation = {
        accountName: `${this.kycDetailsForm.firstName} ${this.kycDetailsForm.lastName}`,
        emailAddress: this.kycDetailsForm.email,
        phoneNumber: this.kycDetailsForm.phoneNumber,
        gender: this.kycDetailsForm.gender,
        dateOfBirth: this.kycDetailsForm.dateOfBirth,
        idType: this.kycDetailsForm.idType,
        idNumber: this.kycDetailsForm.idNumber,
        identity: this.kycDetailsForm.bvn
      }
      const payload = {
        kycDetailsForm: { contactInformation },
        kycStep: this.kycStep
      }
      this.submitKycDetailsStep(payload)
    },
    prefillForm () {
      const user =
        this.userInfo.contacts.find(
          (contact) => contact.contactType === 'natural_person'
        ) || {}
      this.kycDetailsForm = {
        firstName: user.accountName ? this.sliceName(user.accountName)[0] : '',
        lastName: user.accountName ? this.sliceName(user.accountName)[1] : '',
        email: user.email || '',
        bvn: user.identityNumber || '',
        phoneNumber: user.phoneNumber || '',
        gender: user.gender || '',
        dateOfBirth: user.dateOfBirth || '',
        idType: user.idType || '',
        idNumber: user.idNumber || ''
      }
    }
  },
  mounted () {
    if (this.userInfo?.contacts?.length > 0) {
      this.prefillForm()
    }
  }
}
</script>

<style scoped>
p {
  color: #5d5e68;
  font-size: 13px;
}

button:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
